import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import {
  AssetDeletePreview,
  AssetType,
  useDeleteAssetsMutation,
  useLazyGetAssetsQuery,
  usePreviewAssetDeleteMutation,
} from "../store/slices/mapsSlice";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import AssetList from "./AssetList";
import MyPagination from "../Common/MyPagination";

function getErrorMessage(error: any) {
  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.error ||
    "Unable to delete assets."
  );
}

type CircularReferenceIssue = {
  parent_uuid: string;
  parent_display?: string;
  child_uuid: string;
  child_display?: string;
  cycle_path: string[];
  cycle_display?: string[];
};

type DeleteErrorDetails = {
  title: string;
  summary: string;
  issues: CircularReferenceIssue[];
  rawMessage: string;
};

const warningTriangleIcon = faTriangleExclamation.icon;
const warningTriangleViewBox = `0 0 ${warningTriangleIcon[0]} ${warningTriangleIcon[1]}`;
const warningTrianglePath = warningTriangleIcon[4] as string;

function getDeleteErrorDetails(error: any) {
  const payload = error?.data;
  const message = getErrorMessage(error);

  if (
    payload?.error_code === "asset_hierarchy_circular_reference" &&
    Array.isArray(payload.errors)
  ) {
    const issues = payload.errors.filter(
      (issue: any) => issue?.parent_uuid && issue?.child_uuid
    );
    return {
      title: "Circular asset hierarchy",
      summary: `${issues.length} replacement ${
        issues.length === 1 ? "link would" : "links would"
      } make an asset both a parent and a child in the same hierarchy.`,
      issues,
      rawMessage: message,
    };
  }

  return null;
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="border rounded-3 p-3 bg-light flex-grow-1">
      <div className="fw-bold fs-4 text-navy">{value}</div>
      <div className="small text-secondary">{label}</div>
    </div>
  );
}

function ReplacementSelect({
  label,
  help,
  value,
  onChange,
  loadOptions,
  getValueLabels,
  disabled,
}: {
  label: string;
  help: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  loadOptions: (search: string, prevOptions: any[]) => Promise<any>;
  getValueLabels: (values: any[]) => Promise<any[]>;
  disabled?: boolean;
}) {
  const selectedValues = useMemo(() => (value ? [value] : []), [value]);

  return (
    <div className="mb-3">
      <Input
        label={label}
        inputType="select"
        value={selectedValues}
        onChange={([selectedValue]: any[]) => onChange(selectedValue || "")}
        options={undefined}
        loadOptions={loadOptions as any}
        getValueLabels={getValueLabels as any}
        isClearable
        isDisabled={disabled}
        placeholder="Search assets or leave blank to skip"
      />
      <div className="form-text mt-2">{help}</div>
    </div>
  );
}

export default function DeleteAssetsModal({
  show,
  assetUuids,
  assets = [],
  onHide,
  onDeleted,
}: {
  show: boolean;
  assetUuids: string[];
  assets?: AssetType[];
  onHide: () => void;
  onDeleted?: () => void;
}) {
  const normalizedAssetUuids = useMemo(
    () => Array.from(new Set(assetUuids.filter(Boolean))),
    [assetUuids]
  );
  const normalizedAssetUuidsKey = normalizedAssetUuids.join(",");
  const [deleteAssetUuids, setDeleteAssetUuids] = useState<string[]>([]);
  const deleteAssetUuidsKey = deleteAssetUuids.join(",");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dependentReplacementAssetUuid, setDependentReplacementAssetUuid] =
    useState("");
  const [connectedReplacementAssetUuid, setConnectedReplacementAssetUuid] =
    useState("");
  const [contactsReplacementAssetUuid, setContactsReplacementAssetUuid] =
    useState("");
  const [monitorsReplacementAssetUuid, setMonitorsReplacementAssetUuid] =
    useState("");
  const [archiveRelatedEvents, setArchiveRelatedEvents] = useState(false);
  const [deleteErrorDetails, setDeleteErrorDetails] =
    useState<DeleteErrorDetails | null>(null);
  const [assetsToDeletePagination, setAssetsToDeletePagination] = useState({
    page: 0,
    perPage: 12,
  });
  const [previewAssetDelete, previewResult] = usePreviewAssetDeleteMutation();
  const [deleteAssets, deleteResult] = useDeleteAssetsMutation();
  const [getAssets] = useLazyGetAssetsQuery();

  useEffect(() => {
    if (!show) {
      return;
    }

    setShowConfirmModal(false);
    setDeleteErrorDetails(null);
    setDependentReplacementAssetUuid("");
    setConnectedReplacementAssetUuid("");
    setContactsReplacementAssetUuid("");
    setMonitorsReplacementAssetUuid("");
    setArchiveRelatedEvents(false);
    setAssetsToDeletePagination({ page: 0, perPage: 12 });
    setDeleteAssetUuids(normalizedAssetUuids);
  }, [show, normalizedAssetUuidsKey]);

  useEffect(() => {
    if (!show || deleteAssetUuids.length === 0) {
      return;
    }

    previewAssetDelete({ asset_uuids: deleteAssetUuids });
  }, [show, deleteAssetUuidsKey, previewAssetDelete]);

  const loadReplacementAssetOptions = useCallback(async (
    search: string,
    prevOptions: any[]
  ) => {
    const perPage = 25;
    const page = Math.floor(prevOptions.length / perPage);
    const response: any = await getAssets({
      page,
      perPage,
      search,
      ignore: deleteAssetUuids,
    });
    const data = response.data || { assets: [], total: 0 };
    const options = (data.assets || []).map((asset: AssetType) => ({
      value: asset.Uuid,
      label: asset.Label || asset.Reference || asset.Uuid,
    }));

    return {
      options,
      hasMore: prevOptions.length + options.length < (data.total || 0),
    };
  }, [getAssets, deleteAssetUuidsKey]);

  const getReplacementAssetValueLabels = useCallback(async (values: any[]) => {
    const response: any = await getAssets({
      page: 0,
      perPage: values.length || 1,
      assets: values,
    });
    return (response.data?.assets || []).map((asset: AssetType) => ({
      value: asset.Uuid,
      label: asset.Label || asset.Reference || asset.Uuid,
    }));
  }, [getAssets]);

  const preview: AssetDeletePreview | undefined = deleteAssetUuids.length
    ? previewResult.data?.preview
    : undefined;
  const assetsBlockedByRunningEvents = useMemo(() => {
    if (!preview) {
      return [];
    }
    return preview.asset_uuids.filter(
      (uuid) => (preview.per_asset[uuid]?.running_event_uuids?.length || 0) > 0
    );
  }, [preview]);
  const canOpenConfirm =
    deleteAssetUuids.length > 0 &&
    (preview?.totals.running_events || 0) === 0 &&
    !deleteResult.isLoading;
  const canDelete = canOpenConfirm;
  const visibleAssets = preview?.assets?.length
    ? preview.assets
    : assets.filter((asset) => asset.Uuid && deleteAssetUuids.includes(asset.Uuid));
  const paginatedVisibleAssets = visibleAssets.slice(
    assetsToDeletePagination.page * assetsToDeletePagination.perPage,
    (assetsToDeletePagination.page + 1) * assetsToDeletePagination.perPage
  );

  const handleHide = () => {
    setShowConfirmModal(false);
    onHide();
  };

  const handleDelete = async () => {
    if (!canDelete) {
      return;
    }

    const response: any = await deleteAssets({
      asset_uuids: deleteAssetUuids,
      dependent_replacement_asset_uuid:
        dependentReplacementAssetUuid || undefined,
      connected_replacement_asset_uuid: connectedReplacementAssetUuid || undefined,
      contacts_replacement_asset_uuid: contactsReplacementAssetUuid || undefined,
      monitors_replacement_asset_uuid: monitorsReplacementAssetUuid || undefined,
      archive_related_events: archiveRelatedEvents,
      confirmed: true,
    });

    if (response.error) {
      const details = getDeleteErrorDetails(response.error);
      if (details) {
        setDeleteErrorDetails(details);
        setShowConfirmModal(false);
      } else {
        toast.error(getErrorMessage(response.error));
      }
      return;
    }

    toast.success(
      deleteAssetUuids.length === 1
        ? "Asset deleted successfully"
        : "Assets deleted successfully"
    );
    onDeleted?.();
    setShowConfirmModal(false);
    handleHide();
  };

  const handleExcludeAssetsWithRunningEvents = () => {
    if (assetsBlockedByRunningEvents.length === 0) {
      return;
    }

    const blocked = new Set(assetsBlockedByRunningEvents);
    setDeleteAssetUuids((uuids) => uuids.filter((uuid) => !blocked.has(uuid)));
    setAssetsToDeletePagination({ page: 0, perPage: 12 });
  };

  return (
    <>
    <Modal show={show} onHide={handleHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Delete {deleteAssetUuids.length === 1 ? "asset" : "assets"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {previewResult.isLoading ? (
          <div className="d-flex align-items-center gap-2 p-3">
            <Spinner animation="border" size="sm" />
            <span>Checking asset relationships...</span>
          </div>
        ) : previewResult.error ? (
          <div className="alert alert-danger">
            {getErrorMessage(previewResult.error)}
          </div>
        ) : (
          <>
            <div className="alert alert-warning">
              This will permanently delete {deleteAssetUuids.length} asset
              {deleteAssetUuids.length === 1 ? "" : "s"}. Any skipped
              replacements will remove the affected relationships.
            </div>

            {preview && (
              <>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  <SummaryMetric label="Assets" value={preview.totals.assets} />
                  <SummaryMetric
                    label="Dependant assets"
                    value={preview.totals.dependent_assets}
                  />
                  <SummaryMetric
                    label="Connected assets"
                    value={preview.totals.connected_assets}
                  />
                  <SummaryMetric
                    label="Contacts"
                    value={preview.totals.contacts}
                  />
                  <SummaryMetric label="Groups" value={preview.totals.groups} />
                  <SummaryMetric
                    label="Locations"
                    value={preview.totals.locations}
                  />
                  <SummaryMetric label="Events" value={preview.totals.events} />
                  <SummaryMetric
                    label="Running events"
                    value={preview.totals.running_events}
                  />
                  <SummaryMetric
                    label="Archiveable events"
                    value={preview.totals.archive_events}
                  />
                  <SummaryMetric
                    label="Monitors"
                    value={preview.totals.monitors}
                  />
                </div>

                {preview.totals.running_events > 0 && (
                  <div className="alert alert-danger">
                    These assets are attached to running events. Delete is
                    disabled until those events are no longer active or creating.
                    {assetsBlockedByRunningEvents.length > 0 && (
                        <div className="mt-3">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={handleExcludeAssetsWithRunningEvents}
                          >
                            Remove {assetsBlockedByRunningEvents.length} asset
                            {assetsBlockedByRunningEvents.length === 1 ? "" : "s"} with
                            running events from deletion
                          </Button>
                        </div>
                      )}
                  </div>
                )}

                <div className="row">
                  {preview.totals.dependent_assets > 0 && (
                  <div className="col-12">
                    <ReplacementSelect
                      label="Dependant asset replacement"
                      help={
                        <>
                          Choose one asset to become the new parent for all child
                          assets that currently depend on the deleted assets. For
                          example, if deleting "Depot A" and "Van 12" is listed
                          under it, selecting "Depot B" moves "Van 12" under
                          "Depot B". Leave blank to remove those parent-child
                          links.
                        </>
                      }
                      value={dependentReplacementAssetUuid}
                      onChange={setDependentReplacementAssetUuid}
                      loadOptions={loadReplacementAssetOptions}
                      getValueLabels={getReplacementAssetValueLabels}
                    />
                  </div>
                  )}
                  {preview.totals.connected_assets > 0 && (
                  <div className="col-12">
                    <ReplacementSelect
                      label="Connected asset replacement"
                      help={
                        <>
                          Choose one asset to receive the parent connections from
                          the deleted assets. For example, if deleting "Van 12"
                          and it is connected to parent "Depot A", selecting
                          "Van 99" connects "Depot A" to "Van 99" instead.
                          Leave blank to remove those connected-asset links.
                        </>
                      }
                      value={connectedReplacementAssetUuid}
                      onChange={setConnectedReplacementAssetUuid}
                      loadOptions={loadReplacementAssetOptions}
                      getValueLabels={getReplacementAssetValueLabels}
                    />
                  </div>
                  )}
                  {preview.totals.contacts > 0 && (
                  <div className="col-12">
                    <ReplacementSelect
                      label="Contact asset replacement"
                      help={
                        <>
                          Choose one asset to keep contacts linked after deletion.
                          For example, if contacts assigned to "Office A" should
                          now belong to "Office B", select "Office B" here.
                          Leave blank to remove the deleted assets from those
                          contacts without adding a replacement asset.
                        </>
                      }
                      value={contactsReplacementAssetUuid}
                      onChange={setContactsReplacementAssetUuid}
                      loadOptions={loadReplacementAssetOptions}
                      getValueLabels={getReplacementAssetValueLabels}
                    />
                  </div>
                  )}
                  {preview.totals.monitors > 0 && (
                  <div className="col-12">
                    <ReplacementSelect
                      label="Monitor asset replacement"
                      help={
                        <>
                          Choose one asset to keep monitors linked after
                          deletion. For example, if a weather or ping monitor is
                          watching "Depot A", selecting "Depot B" moves that
                          monitor association to "Depot B". Leave blank to
                          remove the deleted assets from those monitors.
                        </>
                      }
                      value={monitorsReplacementAssetUuid}
                      onChange={setMonitorsReplacementAssetUuid}
                      loadOptions={loadReplacementAssetOptions}
                      getValueLabels={getReplacementAssetValueLabels}
                    />
                  </div>
                  )}
                </div>

                {preview.totals.archive_events > 0 && (
                <div className="border rounded-3 p-3 bg-light">
                  <Form.Check
                    type="checkbox"
                    id="archive-related-events"
                    label="Archive related events after deleting asset links"
                    checked={archiveRelatedEvents}
                    disabled={preview.totals.archive_events === 0}
                    onChange={(event) =>
                      setArchiveRelatedEvents(event.currentTarget.checked)
                    }
                  />
                  <div className="form-text mt-2">
                    Closed or resolved related events can be marked as archived
                    during deletion. Running events cannot be archived here and
                    will block asset deletion.
                  </div>
                </div>
                )}
              </>
            )}

            {visibleAssets.length > 0 && (
              <div className="mt-4 mb-3">
                <div className="fw-bold mb-2">Assets to delete</div>
                <AssetList showTable assets={paginatedVisibleAssets} />
                <MyPagination
                  total={visibleAssets.length}
                  defaultPerPage={12}
                  perPageOptions={[12, 24, 48]}
                  forceRefresh={visibleAssets.map((asset) => asset.Uuid).join(",")}
                  onPaginate={(page: number, perPage: number) =>
                    setAssetsToDeletePagination({ page, perPage })
                  }
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={
            !canOpenConfirm ||
            !!previewResult.error ||
            previewResult.isLoading ||
            deleteAssetUuids.length === 0
          }
          onClick={() => {
            setShowConfirmModal(true);
          }}
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal
      show={showConfirmModal}
      onHide={() => setShowConfirmModal(false)}
      size="sm"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm delete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column align-items-center text-center gap-3 py-2">
          <svg
            aria-hidden="true"
            className="text-warning"
            height="96"
            role="img"
            viewBox={warningTriangleViewBox}
            width="96"
          >
            <path fill="currentColor" d={warningTrianglePath} />
          </svg>
          <div className="fw-bold fs-5">
            Confirm deleting {deleteAssetUuids.length} asset
            {deleteAssetUuids.length === 1 ? "" : "s"}
          </div>
          <div className="text-secondary">
            This action cannot be undone.
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" disabled={!canDelete} onClick={handleDelete}>
          {deleteResult.isLoading ? "Deleting..." : "Delete"}
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal
      show={!!deleteErrorDetails}
      onHide={() => setDeleteErrorDetails(null)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{deleteErrorDetails?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-danger mb-3">
          {deleteErrorDetails?.summary}
        </div>
        {!!deleteErrorDetails?.issues.length && (
          <div className="border rounded-3 bg-light p-3 overflow-auto" style={{ maxHeight: 360 }}>
            {deleteErrorDetails.issues.map((issue, index) => (
              <div className="border-bottom pb-3 mb-3" key={`${issue.parent_uuid}-${issue.child_uuid}-${index}`}>
                <div className="d-flex justify-content-between gap-3 mb-2">
                  <div className="fw-bold">Issue {index + 1}</div>
                  <div className="small text-secondary">Replacement link</div>
                </div>
                <code className="d-block text-break mb-2">
                  {issue.parent_display || issue.parent_uuid} -&gt; {issue.child_display || issue.child_uuid}
                </code>
                {!!(issue.cycle_display?.length || issue.cycle_path?.length) && (
                  <>
                    <div className="small text-secondary fw-bold text-uppercase">
                      Circular path
                    </div>
                    <code className="d-block text-break mt-1">
                      {(issue.cycle_display?.length ? issue.cycle_display : issue.cycle_path).join(" -> ")}
                    </code>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        {deleteErrorDetails?.rawMessage && (
          <details className="mt-3">
            <summary className="small text-secondary">Technical message</summary>
            <code className="d-block text-break mt-2">
              {deleteErrorDetails.rawMessage}
            </code>
          </details>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setDeleteErrorDetails(null)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}
